import colors from 'shared/colors';
import { chartHasMultipleSeries, computeLabelStep, dataLabelStyle, escapeHtml, formatNoData, formatTooltipCard, formatTooltipRow, isDateGrouping, nudgeFirstLabel, syncReferenceLinesToAxis } from '@/composables/grid-page/chart/utils';
import { computed } from 'vue';
import { gridPageChartType } from 'shared/constants.json';
import { useI18n } from 'vue-i18n';

export default function useLineChart(result, chart, onPointClick) {
  const { t } = useI18n();
  const hasMultipleSeries = computed(() => chartHasMultipleSeries(chart.value));
  const isStacked = computed(() => hasMultipleSeries.value && (chart.value.stacked ?? true));

  const chartType = computed(() => {
    if (isStacked.value) {
      return 'area';
    }
    return 'line';
  });

  const series = computed(() => {
    if (chart.value.chartType !== gridPageChartType.line) {
      return [];
    }

    if (result.value === null) {
      return [];
    }

    return result.value.series;
  });

  const categories = computed(() => {
    if (result.value === null) {
      return [];
    }

    return result.value.categories;
  });

  const handleCategoryClick = function handleCategoryClick(e) {
    const click = onPointClick?.value;
    if (!click) {
      return;
    }
    const xAxis = this.xAxis[0];
    const index = Math.round(xAxis.toValue(e.chartX - this.plotLeft, true));
    if (index >= 0 && index < categories.value.length) {
      click(index);
    }
  };

  const chartOptions = computed(() => ({
    chart: {
      animation: false,
      type: chartType.value,
      plotBackgroundColor: 'rgba(0,0,0,0)',
      events: {
        click: handleCategoryClick,
        render() {
          nudgeFirstLabel(this);
          syncReferenceLinesToAxis(this, chart.value.referenceLines);
        },
      },
    },
    plotOptions: {
      [chartType.value]: {
        cursor: onPointClick?.value ? 'pointer' : 'default',
        stacking: isStacked.value ? 'normal' : undefined,
        fillOpacity: 0.15,
        dataLabels: {
          enabled: !hasMultipleSeries.value,
          y: -6,
          style: dataLabelStyle,
        },
        point: {
          events: {
            click() {
              const click = onPointClick?.value;
              if (click) {
                click(this.index);
              }
            },
          },
        },
      },
      series: {
        events: {
          hide(e) {
            const chart = this.chart;
            const visibleCount = chart.series.filter((s) => s.visible).length;

            if (visibleCount === 0 && !this.visible) {
              chart.series[e.target.index].show();
            }
          },
        },
      },
    },
    yAxis: {
      title: { text: null },
      labels: { enabled: true },
      gridLineWidth: 1,
      reversedStacks: false,
      stackLabels: {
        enabled: isStacked.value,
        style: dataLabelStyle,
      },
    },
    legend: {
      enabled: hasMultipleSeries.value,
      layout: 'horizontal',
      align: 'center',
      verticalAlign: 'bottom',
      y: 10,
      events: {
        itemClick(e) {
          const chart = this.chart;
          const visibleCount = chart.series.filter((s) => s.visible).length;

          if (visibleCount === 1 && e.legendItem.visible) {
            return false;
          }
          return true;
        },
      },
    },
    xAxis: {
      categories: categories.value,
      lineWidth: 1,
      ...(isDateGrouping(chart.value.groupBy?.groupBy) && { tickInterval: computeLabelStep(categories.value.length) }),
      labels: {
        enabled: true,
        ...(isDateGrouping(chart.value.groupBy?.groupBy)
          ? { autoRotation: false }
          : { autoRotation: [-45], style: { textOverflow: 'ellipsis', width: '80px' } }),
      },
      crosshair: { color: colors.grey.lighten6, zIndex: 0 },
    },
    series: series.value,
    tooltip: {
      shared: true,
      formatter() {
        const points = this.points;
        const noData = t('highcharts.noDataShort');
        if (points.length > 1) {
          const header = `<div style="margin-bottom:6px;color:${colors.grey.darken4}">${escapeHtml(points[0].key)}</div>`;
          const rows = points.filter((p) => p.y !== 0).map((p) => formatTooltipRow(p.color, p.series.name, p.y)).join('');
          return formatTooltipCard(header + (rows || formatNoData(noData)));
        }
        const p = points[0];
        if (p.y === 0) {
          const header = `<div style="margin-bottom:6px;color:${colors.grey.darken4}">${escapeHtml(p.key || p.point.name)}</div>`;
          return formatTooltipCard(header + formatNoData(noData));
        }
        return formatTooltipCard(formatTooltipRow(p.color, p.key || p.point.name, p.y));
      },
    },
  }));

  return { chartOptions };
}
